import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import {
  Search,
  Moon,
  Sun,
  Bookmark,
  Clock,
  Grid3X3,
  Shield,
  Copy,
  ArrowRight,
  Sparkles,
  Command as CommandIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTheme } from "@/hooks/useTheme";
import { useArticlesData } from "@/hooks/useArticlesData";
import { useCategories } from "@/hooks/useCategories";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface CommandItem {
  id: string;
  title: string;
  description?: string;
  category: "actions" | "categories" | "articles";
  icon: React.ReactNode;
  onSelect: () => void;
}

export function GlobalCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { theme, toggleTheme } = useTheme();
  const { articles } = useArticlesData();
  const { categories } = useCategories();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // Listen for global Cmd+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Build searchable items
  const items = useMemo<CommandItem[]>(() => {
    const list: CommandItem[] = [];

    // System Quick Actions
    list.push({
      id: "action-theme",
      title: theme === "light" ? "Switch to Dark Theme" : "Switch to Light Theme",
      description: "Toggle website color appearance",
      category: "actions",
      icon: theme === "light" ? <Moon className="h-4 w-4 text-primary" /> : <Sun className="h-4 w-4 text-primary" />,
      onSelect: () => {
        toggleTheme();
        setOpen(false);
      },
    });

    list.push({
      id: "action-bookmarks",
      title: "My Bookmarks",
      description: "View saved reading list",
      category: "actions",
      icon: <Bookmark className="h-4 w-4 text-primary" />,
      onSelect: () => {
        navigate("/bookmarks");
        setOpen(false);
      },
    });

    list.push({
      id: "action-recent",
      title: "Recent Articles",
      description: "Browse latest publications chronologically",
      category: "actions",
      icon: <Clock className="h-4 w-4 text-primary" />,
      onSelect: () => {
        navigate("/recent-articles");
        setOpen(false);
      },
    });

    list.push({
      id: "action-categories",
      title: "All Categories",
      description: "Explore content verticals",
      category: "actions",
      icon: <Grid3X3 className="h-4 w-4 text-primary" />,
      onSelect: () => {
        navigate("/categories");
        setOpen(false);
      },
    });

    if (isAdmin) {
      list.push({
        id: "action-admin",
        title: "Admin Dashboard",
        description: "Manage articles and analytics",
        category: "actions",
        icon: <Shield className="h-4 w-4 text-primary" />,
        onSelect: () => {
          navigate("/admin");
          setOpen(false);
        },
      });
    }

    list.push({
      id: "action-copy-url",
      title: "Copy Page Link",
      description: "Copy current URL to clipboard",
      category: "actions",
      icon: <Copy className="h-4 w-4 text-primary" />,
      onSelect: () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success("Page link copied to clipboard!");
        setOpen(false);
      },
    });

    // Categories
    categories.forEach((cat) => {
      list.push({
        id: `cat-${cat.id}`,
        title: cat.name,
        description: `Explore all ${cat.name} stories`,
        category: "categories",
        icon: <Grid3X3 className="h-4 w-4 text-muted-foreground" />,
        onSelect: () => {
          navigate(`/category/${cat.slug}`);
          setOpen(false);
        },
      });
    });

    // Articles
    articles.slice(0, 30).forEach((art) => {
      list.push({
        id: `art-${art.id}`,
        title: art.title,
        description: art.category ? `In ${art.category}` : undefined,
        category: "articles",
        icon: <Sparkles className="h-4 w-4 text-muted-foreground" />,
        onSelect: () => {
          navigate(`/article/${art.slug}`);
          setOpen(false);
        },
      });
    });

    return list;
  }, [theme, toggleTheme, categories, articles, isAdmin, navigate]);

  // Filter items by query
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        (item.description && item.description.toLowerCase().includes(q)),
    );
  }, [items, query]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, filtered.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filtered.length) % Math.max(1, filtered.length));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[selectedIndex]) {
        filtered[selectedIndex].onSelect();
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-xl p-0 overflow-hidden bg-card/95 backdrop-blur-2xl border-border/60 shadow-2xl gap-0">
        <DialogTitle className="sr-only">Quick Command Palette</DialogTitle>

        {/* Input Bar */}
        <div className="flex items-center px-4 border-b border-border/40">
          <Search className="h-4 w-4 text-muted-foreground shrink-0 mr-3" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Type a command, category, or search articles…"
            className="h-14 w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            autoFocus
          />
          <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border border-border/60 bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div className="max-h-[60vh] overflow-y-auto p-2 space-y-1">
          {filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No results found for <span className="font-semibold text-foreground">"{query}"</span>
            </div>
          ) : (
            filtered.map((item, i) => {
              const isSelected = i === selectedIndex;
              return (
                <button
                  key={item.id}
                  onClick={item.onSelect}
                  onMouseEnter={() => setSelectedIndex(i)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-colors text-sm group",
                    isSelected
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-foreground hover:bg-accent/60",
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                        isSelected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground",
                      )}
                    >
                      {item.icon}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate leading-snug">{item.title}</p>
                      {item.description && (
                        <p className="text-xs text-muted-foreground truncate opacity-80 mt-0.5">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <ArrowRight
                    className={cn(
                      "h-4 w-4 shrink-0 transition-transform",
                      isSelected ? "opacity-100 translate-x-0 text-primary" : "opacity-0 -translate-x-1",
                    )}
                  />
                </button>
              );
            })
          )}
        </div>

        {/* Footer shortcuts */}
        <div className="px-4 py-2.5 border-t border-border/40 bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded border bg-background px-1 py-0.5 text-[10px]">↑</kbd>
              <kbd className="rounded border bg-background px-1 py-0.5 text-[10px]">↓</kbd>
              <span>navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border bg-background px-1.5 py-0.5 text-[10px]">↵</kbd>
              <span>select</span>
            </span>
          </div>
          <span className="flex items-center gap-1 text-primary font-medium">
            <CommandIcon className="h-3 w-3" /> Bloxida Spotlight
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
